import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  Tabs,
  Tab,
  Checkbox,
  LinearProgress,
  TablePagination
} from '@mui/material';
import { 
  RestoreFromTrash as RestoreIcon, 
  DeleteForever as PermanentDeleteIcon 
} from '@mui/icons-material';
import api from '../services/api';

const RecycleBin = () => {
  const [tab, setTab] = useState(0);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchArchived = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      if (tab === 0) endpoint = '/students/archived';
      else if (tab === 1) endpoint = '/schools/archived';
      else if (tab === 2) endpoint = '/supervisors/archived';
      else if (tab === 3) endpoint = '/co-teachers/archived';
      else endpoint = '/placements/archived';
      
      const response = await api.get(endpoint);
      setItems(response.data);
      setSelected([]); 
      setPage(0); // Reset page on tab change or fetch
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchived();
  }, [tab]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = items.map((n: any) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelectOne = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleRestore = async (id: string) => {
    if (!window.confirm('ต้องการกู้คืนข้อมูลนี้ใช่หรือไม่?')) return;
    setLoading(true);
    try {
      let endpoint = '';
      if (tab === 0) endpoint = `/students/${id}/restore`;
      else if (tab === 1) endpoint = `/schools/${id}/restore`;
      else if (tab === 2) endpoint = `/supervisors/${id}/restore`;
      else if (tab === 3) endpoint = `/co-teachers/${id}/restore`;
      else endpoint = `/placements/${id}/restore`;
      
      if (tab === 0 || tab === 1 || tab === 2 || tab === 3) await api.put(endpoint);
      else await api.post(endpoint);

      fetchArchived();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDelete = async (id: string) => {
    if (!window.confirm('คำเตือน: ข้อมูลจะถูกลบอย่างถาวรและไม่สามารถกู้คืนได้อีก คุณแน่ใจหรือไม่?')) return;
    setLoading(true);
    try {
      let endpoint = '';
      if (tab === 0) endpoint = `/students/${id}/permanent`;
      else if (tab === 1) endpoint = `/schools/${id}/permanent`;
      else if (tab === 2) endpoint = `/supervisors/${id}/permanent`;
      else if (tab === 3) endpoint = `/co-teachers/${id}/permanent`;
      else endpoint = `/placements/${id}/permanent`;
      
      await api.delete(endpoint);
      fetchArchived();
    } catch (err) {
      console.error(err);
      alert('ไม่สามารถลบข้อมูลได้ เนื่องจากมีการใช้งานอยู่ในส่วนอื่นของระบบ');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkPermanentDelete = async () => {
    if (!window.confirm(`คำเตือนร้ายแรง: คุณกำลังจะลบข้อมูลที่เลือกทั้งหมด ${selected.length} รายการ อย่างถาวร! การดำเนินการนี้ไม่สามารถย้อนกลับได้ ยืนยันหรือไม่?`)) return;
    setLoading(true);
    try {
      let baseEndpoint = '';
      if (tab === 0) baseEndpoint = '/students';
      else if (tab === 1) baseEndpoint = '/schools';
      else if (tab === 2) baseEndpoint = '/supervisors';
      else if (tab === 3) baseEndpoint = '/co-teachers';
      else baseEndpoint = '/placements';
      
      await api.post(`${baseEndpoint}/bulk-permanent`, { ids: selected });
      alert('ลบข้อมูลถาวรเรียบร้อยแล้ว');
      fetchArchived();
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการลบข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkRestore = async () => {
    if (!window.confirm(`ยืนยันการกู้คืนข้อมูลที่เลือกทั้งหมด ${selected.length} รายการ?`)) return;
    setLoading(true);
    try {
      let baseEndpoint = '';
      if (tab === 0) baseEndpoint = '/students';
      else if (tab === 1) baseEndpoint = '/schools';
      else if (tab === 2) baseEndpoint = '/supervisors';
      else if (tab === 3) baseEndpoint = '/co-teachers';
      else baseEndpoint = '/placements';
      
      await api.post(`${baseEndpoint}/bulk-restore`, { ids: selected });
      fetchArchived();
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการกู้คืนข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && <LinearProgress sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 2000 }} />}
      <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        ถังขยะและรายการที่ถูกลบ (Recycle Bin)
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        กู้คืนข้อมูล หรือลบข้อมูลทิ้งอย่างถาวรเพื่อล้างฐานข้อมูล
      </Typography>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab label="นักศึกษา" />
        <Tab label="โรงเรียน" />
        <Tab label="อาจารย์นิเทศก์" />
        <Tab label="ครูพี่เลี้ยง" />
        <Tab label="การจัดสรร" />
      </Tabs>

      <TableContainer component={Paper} sx={{ overflowX: 'auto', boxShadow: 2, borderRadius: 2 }}>
        {selected.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'error.50', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'error.100' }}>
            <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 'bold' }}>
              เลือกแล้ว {selected.length} รายการ
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
               <Button 
                variant="outlined" 
                color="success" 
                startIcon={<RestoreIcon />} 
                onClick={handleBulkRestore}
                size="small"
              >
                กู้คืนที่เลือก
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                startIcon={<PermanentDeleteIcon />} 
                onClick={handleBulkPermanentDelete}
                size="small"
              >
                ลบถาวรที่เลือก
              </Button>
            </Box>
          </Box>
        )}
        <Table sx={{ minWidth: 700 }}>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < items.length}
                  checked={items.length > 0 && selected.length === items.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ชื่อรายการ</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>วันที่ลบ</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>การจัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item: any) => {
              const isItemSelected = selected.indexOf(item.id) !== -1;
              return (
                <TableRow 
                  key={item.id} 
                  hover 
                  selected={isItemSelected}
                  onClick={() => handleSelectOne(item.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={isItemSelected} />
                  </TableCell>
                  <TableCell>
                    {tab === 0 ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.prefix}{item.first_name} {item.last_name}</Typography>
                        <Typography variant="caption" color="textSecondary">รหัส: {item.student_code}</Typography>
                      </Box>
                    ) : tab === 1 ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.name}</Typography>
                        <Typography variant="caption" color="textSecondary">{item.affiliation}</Typography>
                      </Box>
                    ) : tab === 2 ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.first_name} {item.last_name}</Typography>
                        <Typography variant="caption" color="textSecondary">{item.department}</Typography>
                      </Box>
                    ) : tab === 3 ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.prefix || ''}{item.first_name} {item.last_name}</Typography>
                        <Typography variant="caption" color="textSecondary">{item.position ? `${item.position} - ` : ''}{item.school_name || 'ไม่ระบุโรงเรียน'}</Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.student_first} {item.student_last} - {item.school_name}</Typography>
                        <Typography variant="caption" color="textSecondary">เทอม {item.semester}/{item.academic_year}</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{item.deleted_at ? new Date(item.deleted_at).toLocaleString('th-TH') : '-'}</TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <IconButton color="success" onClick={() => handleRestore(item.id)} title="กู้คืน">
                        <RestoreIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handlePermanentDelete(item.id)} title="ลบถาวร">
                        <PermanentDeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">ไม่มีรายการในถังขยะ</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={items.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="รายการต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        />
      </TableContainer>
    </Box>
  );
};

export default RecycleBin;
